import React, { useEffect, useRef, useState } from 'react';
import { createCmsBrand, updateCmsBrand, uploadBrandLogo } from '../../api/client';
import SearchableSelect from '../SearchableSelect';

const BrandModal = ({ isOpen, brand, onClose, onBrandSaved }) => {
  const isEdit = Boolean(brand && brand.id);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [origin, setOrigin] = useState('assembled');
  const [country, setCountry] = useState('PK');
  const [logo, setLogo] = useState('🚗');
  const [logoUrl, setLogoUrl] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const fileInputRef = useRef(null);
  const firstInputRef = useRef(null);

  // Sync state whenever modal opens or brand changes
  useEffect(() => {
    if (!isOpen) return;
    if (brand && brand.id) {
      setName(brand.name || '');
      setSlug(brand.slug || '');
      setOrigin(brand.origin || 'assembled');
      setCountry(brand.country || 'PK');
      setLogo(brand.logo || '🚗');
      setLogoUrl(brand.logo_url || '');
    } else {
      setName('');
      setSlug('');
      setOrigin('assembled');
      setCountry('PK');
      setLogo('🚗');
      setLogoUrl('');
    }
    setIsDirty(false);
    setError('');
    setSuccessMsg('');

    const timer = setTimeout(() => {
      firstInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen, brand]);

  // Safe dismiss helper with unsaved changes protection
  const handleSafeClose = () => {
    if (isDirty) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this brand. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setIsDirty(false);
    onClose();
  };

  // Keyboard accessibility: Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleSafeClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDirty]);

  if (!isOpen) return null;

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPG, or WebP). Transparent PNG preferred.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Logo file size exceeds the 5 MB limit.');
      return;
    }

    setUploadingLogo(true);
    setError('');
    try {
      const result = await uploadBrandLogo(file);
      if (result && result.url) {
        setLogoUrl(result.url);
        setIsDirty(true);
      }
    } catch (err) {
      console.error('Brand logo upload failed:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Logo upload failed. Please try again.');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setIsDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Brand name is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      let result;
      if (isEdit) {
        const payload = {
          name: name.trim(),
          origin,
          country: country.trim().toUpperCase() || undefined,
          logo: logo.trim() || '🚗',
          logo_url: logoUrl ? logoUrl.trim() : null,
        };
        result = await updateCmsBrand(brand.id, payload);
        setSuccessMsg('Brand updated successfully.');
      } else {
        const payload = {
          name: name.trim(),
          slug: slug.trim() ? slug.trim().toLowerCase() : undefined,
          origin,
          country: country.trim().toUpperCase() || undefined,
          logo: logo.trim() || '🚗',
          logo_url: logoUrl ? logoUrl.trim() : undefined,
        };
        result = await createCmsBrand(payload);
        setSuccessMsg('Brand created successfully.');
      }

      setIsDirty(false);
      if (onBrandSaved) {
        setTimeout(() => {
          onBrandSaved(result);
        }, 400);
      }
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save brand. Check inputs.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="cms-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleSafeClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="brand-modal-title"
    >
      <div className="cms-modal-dialog" style={{ maxWidth: '620px' }} onClick={(e) => e.stopPropagation()}>
        {/* Sticky Modal Header */}
        <div className="cms-modal-header">
          <h2 id="brand-modal-title">
            {isEdit ? `Edit Brand — ${brand.name}` : 'Add New Automobile Brand'}
          </h2>
          <button
            type="button"
            className="cms-modal-close"
            onClick={handleSafeClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="cms-modal-body">
          <p className="cms-text-muted" style={{ margin: 0, fontSize: '13px' }}>
            {isEdit
              ? 'Update manufacturer brand details and official logo asset. Vehicle associations are preserved.'
              : 'Create a new brand in the database. Vehicles can be immediately created and linked under it.'}
          </p>

          {error && (
            <div className="dash-alert">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          {successMsg && (
            <div className="dash-alert success">
              <i className="fas fa-circle-check" /> {successMsg}
            </div>
          )}

          <form id="cms-brand-form" onSubmit={handleSubmit} className="cms-editor-form">
            <div className="cms-form-group">
              <label htmlFor="brand-name">
                Brand Name <span className="cms-req">*</span>
              </label>
              <input
                id="brand-name"
                ref={firstInputRef}
                type="text"
                placeholder="e.g. Toyota, BYD, Haval, Falcon"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setIsDirty(true);
                  setError('');
                }}
                required
              />
            </div>

            <div className="cms-form-group">
              <label htmlFor="brand-slug">
                Brand Code / Slug {isEdit ? '(Locked)' : '(Optional, auto-generated if left blank)'}
              </label>
              <input
                id="brand-slug"
                type="text"
                placeholder="e.g. toyota, byd, haval"
                value={slug}
                disabled={isEdit}
                className={isEdit ? 'cms-input-readonly' : ''}
                onChange={(e) => {
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'));
                  setIsDirty(true);
                }}
              />
              {isEdit && (
                <span className="cms-section-note" style={{ fontSize: '11px' }}>
                  Brand code is locked to preserve existing vehicle records, comparison metrics, and catalog filters.
                </span>
              )}
            </div>

            <div className="cms-form-row" style={{ padding: 0 }}>
              <div className="cms-form-group">
                <label htmlFor="brand-origin">Origin Classification</label>
                <SearchableSelect
                  id="brand-origin"
                  value={origin}
                  onChange={(e) => {
                    setOrigin(e.target.value);
                    setIsDirty(true);
                  }}
                  options={[
                    { value: 'assembled', label: 'Assembled in Pakistan', icon: '🇵🇰' },
                    { value: 'chinese', label: 'Chinese Brand', icon: '🇨🇳' },
                    { value: 'imported', label: 'Imported / CBU', icon: '🌐' },
                  ]}
                  placeholder="Select origin..."
                />
              </div>

              <div className="cms-form-group">
                <label htmlFor="brand-country">Country Code</label>
                <input
                  id="brand-country"
                  type="text"
                  maxLength={4}
                  placeholder="e.g. PK, CN, JP, KR, DE"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value.toUpperCase());
                    setIsDirty(true);
                  }}
                />
              </div>

              <div className="cms-form-group">
                <label htmlFor="brand-logo-emoji">Fallback Emoji</label>
                <SearchableSelect
                  id="brand-logo-emoji"
                  value={logo}
                  onChange={(e) => {
                    setLogo(e.target.value);
                    setIsDirty(true);
                  }}
                  options={[
                    { value: '🚗', label: 'Standard Car', icon: '🚗' },
                    { value: '⚡', label: 'Electric Vehicle', icon: '⚡' },
                    { value: '🚙', label: 'SUV / Crossover', icon: '🚙' },
                    { value: '🏎️', label: 'Sports Car', icon: '🏎️' },
                    { value: '🚚', label: 'Commercial / Truck', icon: '🚚' },
                  ]}
                  placeholder="Select emoji..."
                />
              </div>
            </div>

            {/* Manufacturer Logo Upload */}
            <div className="cms-form-group" style={{ paddingTop: '8px' }}>
              <label>Official Manufacturer Logo</label>
              <div className="cms-image-upload-wrap">
                <div className="cms-img-preview-box" style={{ background: '#ffffff', padding: '4px' }}>
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Brand logo preview"
                      className="cms-img-preview"
                      style={{ objectFit: 'contain' }}
                    />
                  ) : (
                    <div className="cms-img-placeholder">
                      <span style={{ fontSize: '24px' }}>{logo || '🚗'}</span>
                    </div>
                  )}
                </div>
                <div className="cms-image-upload-content">
                  <div className="cms-image-upload-buttons">
                    <button
                      type="button"
                      className="btn-upload"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <>
                          <i className="fas fa-spinner fa-spin" /> Uploading…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-arrow-up-from-bracket" />{' '}
                          {logoUrl ? 'Replace logo' : 'Upload logo'}
                        </>
                      )}
                    </button>
                    {logoUrl && !uploadingLogo && (
                      <button
                        type="button"
                        className="btn-remove-img"
                        onClick={handleRemoveLogo}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="cms-img-help">PNG, JPG, WEBP. Max 5 MB. Transparent PNG preferred.</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleLogoFileChange}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Sticky Modal Footer */}
        <div className="cms-modal-footer">
          <div className="cms-modal-footer-left">
            {isDirty && (
              <span className="cms-unsaved-badge">
                <i className="fas fa-circle-dot" /> Unsaved changes
              </span>
            )}
          </div>
          <div className="cms-modal-footer-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleSafeClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="cms-brand-form"
              className="btn-save"
              disabled={saving || (!isDirty && isEdit)}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : isEdit ? (
                'Save Brand'
              ) : (
                'Create Brand'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandModal;
